// frontend/src/pages/AccountSettings.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Bell, Save } from 'lucide-react';

// shadcn UI components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Spinner } from '../components/ui/spinner';

// Layout component
import PageContainer from '../components/ui/layout/PageContainer/PageContainer';

// Context and stores
import { useAuth } from '../contexts/AuthContext';
import useSettingsStore from '../stores/SettingsStore';
import { cn } from '../utils/cn';

const AccountSettings = () => {
  const { currentUser, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Navigation state
  const [activeSection, setActiveSection] = useState('profile');
  const [ready, setReady] = useState(false);

  // Section refs for scroll-to functionality
  const sectionRefs = {
    profile: useRef(null),
    notifications: useRef(null)
  };

  // Store calculated section positions
  const sectionPositions = useRef({});

  // ===== PROFILE SECTION =====
  const [isEditing, setIsEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: ''
  });
  const [originalProfileForm, setOriginalProfileForm] = useState({});

  const { updateUserProfile, savingProfile } = useSettingsStore();

  // Initialize profile form from currentUser
  useEffect(() => {
    if (currentUser) {
      const formData = {
        name: currentUser.name || '',
        email: currentUser.email || ''
      };
      setProfileForm(formData);
      setOriginalProfileForm(formData);
    }
  }, [currentUser]);

  // Canonical: savingProfile flag watcher — reset baseline + exit edit on server confirmation
  const prevSavingProfile = useRef(false);
  useEffect(() => {
    if (prevSavingProfile.current && !savingProfile) {
      setOriginalProfileForm(profileForm);
      setIsEditing(false);
    }
    prevSavingProfile.current = savingProfile;
  }, [savingProfile, profileForm]);

  // Check for profile form changes
  const hasProfileChanges = Object.keys(profileForm).some(key =>
    profileForm[key] !== (originalProfileForm[key] || '')
  );

  // Handle profile edit
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setProfileForm(originalProfileForm);
    setIsEditing(false);
  };

  const handleSave = () => {
    updateUserProfile(profileForm.name, profileForm.email);
  };

  // ===== NOTIFICATIONS SECTION =====
  const {
    userNotificationPreferences,
    loadingUserNotificationPrefs,
    loadUserNotificationPreferences,
    createDefaultUserNotificationPreferences,
    updateUserNotificationPreference,
    isUserNotificationEnabled
  } = useSettingsStore();

  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  // Notification types
  const notificationTypes = [
    {
      event_type: 'appointment_scheduled',
      label: 'New Appointments',
      description: 'Get notified when new appointments are scheduled'
    },
    {
      event_type: 'appointment_rescheduled',
      label: 'Rescheduled Appointments',
      description: 'Get notified when appointments are rescheduled'
    },
    {
      event_type: 'appointment_cancelled',
      label: 'Cancelled Appointments',
      description: 'Get notified when appointments are cancelled'
    }
  ];

  // Load notification preferences
  useEffect(() => {
    if (!hasAttemptedLoad) {
      loadUserNotificationPreferences();
      setHasAttemptedLoad(true);
    }
  }, [hasAttemptedLoad, loadUserNotificationPreferences]);

  // Create default preferences if none exist
  useEffect(() => {
    if (
      hasAttemptedLoad &&
      !loadingUserNotificationPrefs &&
      userNotificationPreferences.length === 0
    ) {
      createDefaultUserNotificationPreferences();
    }
  }, [
    hasAttemptedLoad,
    loadingUserNotificationPrefs,
    userNotificationPreferences,
    createDefaultUserNotificationPreferences
  ]);

  const togglePreference = (eventType) => {
    const currentEnabled = isUserNotificationEnabled(eventType, 'email');
    const newEnabled = !currentEnabled;
    updateUserNotificationPreference(eventType, 'email', newEnabled);
  };

  // Handle ?section= query param
  useEffect(() => {
    const sectionParam = searchParams.get('section');
    if (sectionParam) {
      const validSections = ['profile', 'notifications'];
      if (validSections.includes(sectionParam)) {
        setActiveSection(sectionParam);
        searchParams.delete('section');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  // Navigation sections
  const navSections = [
    { id: 'profile', label: 'Personal Information' },
    { id: 'notifications', label: 'Email Notifications' }
  ];

  // Mark as ready after first render
  useEffect(() => {
    setReady(true);
  }, []);

  // Calculate section positions once when ready
  useEffect(() => {
    if (!ready) return;

    const calculatePositions = () => {
      const section = sectionRefs.profile?.current;
      if (!section) return;
      const contentDiv = section.closest('.overflow-y-auto');
      if (!contentDiv) return;

      const containerTop = contentDiv.scrollTop;
      const positions = {};

      Object.entries(sectionRefs).forEach(([id, ref]) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const parentRect = contentDiv.getBoundingClientRect();
          positions[id] = rect.top - parentRect.top + containerTop;
        }
      });

      sectionPositions.current = positions;
    };

    setTimeout(calculatePositions, 100);
  }, [ready]);

  // Scroll spy
  useEffect(() => {
    if (!ready) return;

    const section = sectionRefs.profile?.current;
    if (!section) return;
    const contentDiv = section.closest('.overflow-y-auto');
    if (!contentDiv) return;

    const handleScroll = () => {
      const scrollTop = contentDiv.scrollTop;
      const scrollHeight = contentDiv.scrollHeight;
      const clientHeight = contentDiv.clientHeight;
      const positions = sectionPositions.current;

      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

      if (isAtBottom) {
        setActiveSection(prev => prev !== 'notifications' ? 'notifications' : prev);
        return;
      }

      const sectionOrder = ['notifications', 'profile'];

      for (const sectionId of sectionOrder) {
        const sectionTop = positions[sectionId];
        if (sectionTop !== undefined && scrollTop >= sectionTop - 100) {
          setActiveSection(prev => prev !== sectionId ? sectionId : prev);
          break;
        }
      }
    };

    contentDiv.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      contentDiv.removeEventListener('scroll', handleScroll);
    };
  }, [ready]);

  // Scroll to section
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Loading state
  if (loading || !currentUser) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <Spinner className="size-16 text-primary mx-auto mb-4" />
            <p className="text-foreground text-lg">Loading account settings...</p>
            <p className="text-muted-foreground text-sm mt-2">Retrieving your information</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer heightMode="viewport" maxWidth="full" padding="none">
      <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full pt-12 pl-36 pr-36 flex flex-col">
            {/* Header */}
            <div className="mb-6">
              <div className="space-y-2">
                <h1 className="text-4xl font-bold text-foreground">Account Settings</h1>
                <p className="text-muted-foreground">
                  Manage your personal information and notification preferences
                </p>
              </div>
            </div>

            {/* Side Nav and Content */}
            <div className="flex-1 flex min-h-0">
              {/* Side Navigation */}
              <div className="w-40 flex-shrink-0">
                <div className="space-y-1 sticky top-0">
                  {navSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        "block w-full text-left text-sm py-1 transition-colors",
                        activeSection === section.id
                          ? "text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content - scrollable */}
              <div className="flex-1 min-w-0 overflow-y-auto pl-2 space-y-10 pb-12">
                {/* Personal Information Section */}
                <div ref={sectionRefs.profile} data-section="profile" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <User className="w-5 h-5" />
                      Personal Information
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {isEditing
                        ? 'Update your personal information below'
                        : 'View and manage your account information'}
                    </p>
                  </div>

                  <div className="space-y-4">
                    {/* Name Field */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Name</Label>
                      {isEditing ? (
                        <Input
                          type="text"
                          value={profileForm.name}
                          onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                          placeholder="Enter your name"
                          className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md">
                          <p className="text-sm text-foreground">{currentUser?.name || 'Not set'}</p>
                        </div>
                      )}
                    </div>

                    {/* Email Field */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Email Address</Label>
                      {isEditing ? (
                        <Input
                          type="email"
                          value={profileForm.email}
                          onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                          placeholder="Enter your email"
                          className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                        />
                      ) : (
                        <div className="px-3 py-2 bg-muted rounded-md">
                          <p className="text-sm text-foreground">{currentUser?.email || 'Not set'}</p>
                        </div>
                      )}
                    </div>

                    {/* Role Field (read-only) */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Role</Label>
                      <div className="px-3 py-2 bg-muted rounded-md">
                        <p className="text-sm text-foreground capitalize">{currentUser?.role || 'Not set'}</p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-2">
                      {isEditing ? (
                        <>
                          <Button onClick={handleSave} disabled={!hasProfileChanges || savingProfile}>
                            {savingProfile ? (
                              <>
                                <Spinner className="w-4 h-4 mr-2" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="w-4 h-4 mr-2" />
                                Save Changes
                              </>
                            )}
                          </Button>
                          <Button onClick={handleCancel} variant="outline" disabled={savingProfile}>
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button onClick={handleEdit}>
                          Edit Profile
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Email Notifications Section */}
                <div ref={sectionRefs.notifications} data-section="notifications" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Bell className="w-5 h-5" />
                      Email Notifications
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Choose which email notifications you want to receive. Notifications will be sent to{' '}
                      <span className="font-medium text-foreground">{currentUser?.email}</span>
                    </p>
                  </div>

                  {loadingUserNotificationPrefs && userNotificationPreferences.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Spinner className="size-12 text-primary mx-auto mb-4" />
                      <p className="text-sm text-muted-foreground">Loading notification preferences...</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notificationTypes.map((type) => {
                        const enabled = isUserNotificationEnabled(type.event_type, 'email');

                        return (
                          <div key={type.event_type} className="flex items-center justify-between p-4 border border-border rounded-lg bg-muted/20">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-sm font-medium text-foreground">{type.label}</h4>
                                {enabled && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                    Active
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">{type.description}</p>
                            </div>
                            <Switch
                              checked={enabled}
                              onCheckedChange={() => togglePreference(type.event_type)}
                              disabled={loadingUserNotificationPrefs}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default AccountSettings;
